import { Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TTDCampaign } from '@feathr/blackbox';
import { Tooltip, UniversalCreatableSelect } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import type { IWithRecommendedValue } from '../../AdWizard.useRecommendedValue';
import useRecommendedValue from '../../AdWizard.useRecommendedValue';

const MIN_FREQUENCY_CAP = 1;
const MAX_FREQUENCY_CAP = 1000;
const DEFAULT_VALUE = 9;
const LIFETIME_PERIOD = '2147483647';

export interface IFreqCapSelectProps extends IWithRecommendedValue {
  campaign: TTDCampaign;
}

interface IOption {
  value: string;
  label: string;
}

function compareOption(inputValue: string, option: IOption): boolean {
  return inputValue === option.value;
}

function isValidNewOption(
  inputValue: string,
  _: IOption | IOption[] | null,
  options: readonly IOption[],
): boolean {
  const asNumber = Number(inputValue);
  return !(
    !inputValue ||
    // Impressions are counted in whole numbers, so this also rules out NaN and Infinity
    !Number.isInteger(asNumber) ||
    !(asNumber > 0 && asNumber <= MAX_FREQUENCY_CAP) ||
    options.some((option) => compareOption(inputValue, option))
  );
}

function FreqCapSelect({ campaign, recommendedValue }: Readonly<IFreqCapSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const { shouldUseRecommendedValue, setHasUserSelected, hasRecommendation } =
    useRecommendedValue(recommendedValue);
  const [validationError, setValidationError] = useState<string | undefined>(undefined);

  const cap = shouldUseRecommendedValue
    ? recommendedValue
    : campaign.get('bidding_strategy').freq_cap;
  const period = String(campaign.get('bidding_strategy').freq_period);

  function createOption(inputValue: string): IOption {
    const asNumeral = numeral(Number(inputValue));
    return {
      value: asNumeral.format('0'),
      label: t('{{count, number}} impression', { count: Number(inputValue) }),
    };
  }

  function formatCreateLabel(inputValue: string): string {
    return t('Use custom frequency cap: {{count, number}} impression', {
      count: Number(inputValue),
    });
  }

  function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /*
   * Blur commits whatever was typed without going through isValidNewOption, so a
   * fractional, out-of-range or non-numeric value still reaches this handler. The cap
   * is always committed as a usable whole number, so name the value that was used
   * rather than rewriting what was typed silently. Each case gets its own message
   * because only one of them is actually about the allowed range.
   */
  function getAdjustmentMessage(typedCap: number, committedCap: number): string | undefined {
    if (typedCap === committedCap) {
      return undefined;
    }

    if (isNaN(typedCap)) {
      return t('Frequency cap must be a number. Keeping {{committed, number}}.', {
        committed: committedCap,
      });
    }

    if (typedCap < MIN_FREQUENCY_CAP || typedCap > MAX_FREQUENCY_CAP) {
      return t(
        'Frequency cap must be between {{min, number}} and {{max, number}}. Using {{committed, number}} instead.',
        {
          committed: committedCap,
          max: MAX_FREQUENCY_CAP,
          min: MIN_FREQUENCY_CAP,
        },
      );
    }

    return t('Frequency cap must be a whole number. Using {{committed, number}} instead.', {
      committed: committedCap,
    });
  }

  function onChangeFreqCap(value: string | number): void {
    const newCap = Number(value);
    // Impressions are whole numbers, so round before clamping between min and max cap
    const adjustedCap = clamp(Math.round(newCap), MIN_FREQUENCY_CAP, MAX_FREQUENCY_CAP);
    /*
     * Typing letters is how a searchable select is filtered, and blur commits that
     * leftover text, so non-numeric input is a mistype rather than a new cap. Keep
     * the current cap instead of resetting a deliberate value to the default.
     */
    const committedCap = isNaN(newCap) ? cap ?? DEFAULT_VALUE : adjustedCap;

    setValidationError(getAdjustmentMessage(newCap, committedCap));

    campaign.set({
      bidding_strategy: {
        ...campaign.get('bidding_strategy'),
        freq_cap: committedCap,
      },
    });

    setHasUserSelected(true);
  }

  function handleSelectSingle(option: { value: string; label: string }): void {
    onChangeFreqCap(option.value);
  }

  function getHelpText(period: string): string {
    if (period === LIFETIME_PERIOD) {
      return t(
        'The maximum number of times Feathr may show ads from this campaign to the same user for the duration of the campaign.',
      );
    }
    return t(
      'The maximum number of times Feathr may show ads from this campaign to the same user in {{article}} {{period}}-hour period.',
      {
        article: period.includes('8') ? t('an') : t('a'),
        period: Math.ceil(campaign.get('bidding_strategy').freq_period / 60),
      },
    );
  }

  const maximumOption: IOption = {
    value: String(MAX_FREQUENCY_CAP),
    label: t('{{count, number}} impression', { count: MAX_FREQUENCY_CAP }),
  };

  // If there's a recommended value, only offer that as an option
  const defaultOptions: IOption[] = hasRecommendation
    ? [
        {
          value: String(recommendedValue),
          label: t('{{count, number}} impression', { count: recommendedValue }),
        },
      ]
    : [
        { value: '4', label: t('{{count, number}} impression', { count: 4 }) },
        { value: '9', label: t('{{count, number}} impression', { count: 9 }) },
        { value: '16', label: t('{{count, number}} impression', { count: 16 }) },
        { value: '18', label: t('{{count, number}} impression', { count: 18 }) },
      ];

  function getValue(): IOption {
    // Only show recommended label if user hasn't selected anything yet
    if (shouldUseRecommendedValue) {
      return {
        value: String(recommendedValue),
        label: t('{{count, number}} impression (recommended)', { count: recommendedValue }),
      };
    }

    return (
      // return matching option or
      defaultOptions.find((option) => option.value === String(cap)) ||
      // if option exceeds max cap, return maximum option (1000 impressions) otherwise return custom option
      (cap && cap > MAX_FREQUENCY_CAP
        ? maximumOption
        : { value: String(cap), label: t('{{count, number}} impression', { count: cap }) })
    );
  }

  const label = (
    <>
      {t('Frequency cap')} <Tooltip title={getHelpText(period)}>{getIconForAction('info')}</Tooltip>
    </>
  );

  /*
   * The preset options are only a starting point — any value in range can be typed
   * into the select. Say so, since nothing else in the UI reveals it and the presets
   * are far too low for a lifetime frequency period.
   */
  const customValueHelpText = t(
    'Select an option or type any number between {{min, number}} and {{max, number}}.',
    {
      max: MAX_FREQUENCY_CAP,
      min: MIN_FREQUENCY_CAP,
    },
  );

  const helpText = hasRecommendation ? (
    <Stack gap={'xs'}>
      {t('Original frequency cap: {{cap}}', {
        cap: t('{{count, number}} impression', {
          count: campaign.initialAttributes.bidding_strategy?.freq_cap,
        }),
      })}
      {customValueHelpText}
    </Stack>
  ) : (
    customValueHelpText
  );

  return (
    <UniversalCreatableSelect<IOption>
      createOption={createOption}
      defaultOptions={defaultOptions}
      formatCreateLabel={formatCreateLabel}
      helpPlacement={'top'}
      helpText={helpText}
      isFullWidth={true}
      isValidNewOption={isValidNewOption}
      label={label}
      name={'frequency-cap'}
      onSelectSingle={handleSelectSingle}
      validationError={validationError}
      value={getValue()}
    />
  );
}

export default observer(FreqCapSelect);
